/**
 * Admin → Users tab — pure presentation model (E05-F01-T03 + E05-F01-T04).
 *
 * Framework-free, side-effect-free helpers shared by the React `UsersTab` and its
 * unit tests. They encode the legacy `dashboard.js` rendering + dialog rules
 * verbatim (renderUsersList / openUserDialog / saveUser) so the v2 SPA behaves
 * 1:1 with the n8n SPA:
 *
 *   T03 (the table):
 *     - {@link avatarLabel}  — the avatar bubble text: `user.avatar` when set,
 *       else the name initials (first letter of up to two words, upper), else '?'
 *       (legacy `u.avatar || u.name?.split(' ')...substring(0,2).toUpperCase() || '?'`);
 *     - {@link roleLabel} / {@link roleBadgeClass} — the role pill label + colour,
 *       reusing the CANONICAL maps from `@eer/shared` (admin=purple, teamlead=blue,
 *       recruiter=gray — T03 acceptance #3), with the unknown-role fall-backs the
 *       legacy `colors[role] || colors.recruiter` / `roleLabels[role] || role` had;
 *     - {@link isSelf} — true when the row is the current user (legacy
 *       `state.currentUser.id === u.id`) → drives the `(you)` tag + hidden Delete.
 *
 *   T04 (the dialog form + mailbox):
 *     - {@link formFromUser} — seeds the Add/Edit dialog. Add (`user==null`) →
 *       empty fields, role 'recruiter', empty mailbox (legacy openUserDialog(null));
 *       Edit → seeds name/email/role/team/avatar/signature from the row and the
 *       mailbox sub-form from `user.mailbox` (password fields are ALWAYS blank — the
 *       v2 API never returns secrets, T02 hardening; legacy seeded them but v2
 *       cannot, so "leave blank to keep current");
 *     - {@link mailboxIsExpanded} — the IMAP/SMTP section starts expanded on Edit
 *       iff the user already has an imap_host (legacy `if (mb.imap_host) ...remove
 *       'hidden'`);
 *     - {@link bodyFromForm} — builds the create/update request body. name+email
 *       trimmed; password sent ONLY when non-empty (Edit "Leave empty to keep
 *       current" → omit; Add requires it, enforced by the component); team_id/avatar
 *       trimmed→null when blank; the `mailbox` object is attached ONLY when
 *       imap_host is filled (legacy `if (mailbox.imap_host) userData.mailbox =
 *       mailbox` — T04 acceptance #2 "IMAP Host пуст: строка не трогается").
 *
 * The canonical role label/badge constants come from `@eer/shared` so the table
 * pill and the API speak ONE set of roles.
 */
import {
  ADMIN_USER_ROLE_LABELS,
  ADMIN_USER_ROLE_BADGE_CLASS,
  ADMIN_USER_DEFAULT_ROLE,
  type AdminUserDTO,
  type AdminUserRole,
  type CreateAdminUserInput,
  type UpdateAdminUserInput,
} from '@eer/shared';
// Pure (framework-free) rich-text comparison — `<p></p>`, `<p><br></p>` and '' are
// all the SAME empty document, which a raw string compare would miss.
import { isSameRichText } from '../ui/richTextEditorModel.js';

// ---------------------------------------------------------------------------
// Avatar bubble (renderUsersList)
// ---------------------------------------------------------------------------

/**
 * The avatar bubble label. Legacy `u.avatar || u.name?.split(' ').map(w =>
 * w[0]).join('').substring(0,2).toUpperCase() || '?'`: the explicit avatar wins,
 * else the name initials (≤2 chars, upper), else the literal `?`.
 */
export function avatarLabel(user: Pick<AdminUserDTO, 'avatar' | 'name'>): string {
  const explicit = user.avatar?.trim();
  if (explicit) return explicit;
  const initials = (user.name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0]!)
    .join('')
    .substring(0, 2)
    .toUpperCase();
  return initials || '?';
}

// ---------------------------------------------------------------------------
// Role pill (renderUsersList roleBadge / roleLabels)
// ---------------------------------------------------------------------------

/** Role pill label; unknown role falls back to the raw string (legacy parity). */
export function roleLabel(role: string): string {
  return ADMIN_USER_ROLE_LABELS[role as AdminUserRole] ?? role;
}

/** Role pill colour class; unknown role falls back to the recruiter (gray) class. */
export function roleBadgeClass(role: string): string {
  return (
    ADMIN_USER_ROLE_BADGE_CLASS[role as AdminUserRole] ?? ADMIN_USER_ROLE_BADGE_CLASS.recruiter
  );
}

/**
 * True when the row belongs to the currently signed-in user (legacy
 * `state.currentUser && state.currentUser.id === u.id`). Drives the `(you)` tag
 * and hides the Delete action (T03 acceptance #2). String-compared like the
 * legacy `String(...) === String(...)`.
 */
export function isSelf(rowId: string, currentUserId: string | null | undefined): boolean {
  if (currentUserId == null) return false;
  return String(rowId) === String(currentUserId);
}

// ---------------------------------------------------------------------------
// Dialog form (openUserDialog / saveUser)
// ---------------------------------------------------------------------------

/** The IMAP/SMTP mailbox sub-form (legacy #user-mailbox-fields). */
export interface UserMailboxForm {
  imapHost: string;
  imapPort: string;
  imapUser: string;
  imapPassword: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
}

/** Mutable dialog-form state (mirrors the legacy #user-dialog fields). */
export interface UserForm {
  name: string;
  email: string;
  password: string;
  role: AdminUserRole;
  teamId: string;
  avatar: string;
  emailSignature: string;
  /**
   * Whether {@link emailSignature} reflects the SAVED value (E10-F04-T10).
   *
   * The signature is not in the list DTO — it is fetched per user when the Edit
   * dialog opens (`useUserSignature`), so between opening the dialog and that
   * response the field is legitimately empty-but-unknown. The flag separates that
   * from "the admin cleared the signature": `bodyFromForm` sends `email_signature`
   * ONLY when it is true. Without it, saving a role change while the fetch is still
   * in flight (or failed) would write the blank editor over the stored signature —
   * the data-loss half of the bug this task closes.
   *
   * `true` from the start on Add (nothing to load) and the moment the admin edits
   * the field (their intent wins over a late response).
   */
  signatureLoaded: boolean;
  mailbox: UserMailboxForm;
}

const EMPTY_MAILBOX: UserMailboxForm = {
  imapHost: '',
  imapPort: '',
  imapUser: '',
  imapPassword: '',
  smtpHost: '',
  smtpPort: '',
  smtpUser: '',
  smtpPassword: '',
};

/** A number→string field value (legacy `mb.imap_port || ''` — null/0 → ''). */
function numField(value: number | null | undefined): string {
  return value ? String(value) : '';
}

/**
 * Seed the dialog form. For a NEW user (`user == null`) every field is blank, the
 * role is the legacy Add default ('recruiter') and the mailbox sub-form is empty
 * (legacy openUserDialog(null)). For an EDIT the fields are seeded from the row;
 * the password is ALWAYS blank ("Leave empty to keep current"); the mailbox
 * sub-form is seeded from `user.mailbox` EXCEPT the passwords, which the v2 API
 * never returns (T02 hardening) so they stay blank.
 *
 * The signature starts blank on an Edit too — but with `signatureLoaded:false`,
 * because it is NOT part of the list DTO (heavy `data:` logo) and arrives from
 * `GET /admin/users/:id/email-signature`; {@link applyLoadedSignature} fills it in
 * (E10-F04-T10). On Add there is nothing to load, so the flag is already true.
 */
export function formFromUser(user: AdminUserDTO | null): UserForm {
  if (user == null) {
    return {
      name: '',
      email: '',
      password: '',
      role: ADMIN_USER_DEFAULT_ROLE,
      teamId: '',
      avatar: '',
      emailSignature: '',
      // Nothing to fetch for a brand-new user: the blank editor IS the value.
      signatureLoaded: true,
      mailbox: { ...EMPTY_MAILBOX },
    };
  }
  const mb = user.mailbox;
  return {
    name: user.name ?? '',
    email: user.email ?? '',
    password: '',
    role: (user.role as AdminUserRole) ?? ADMIN_USER_DEFAULT_ROLE,
    teamId: user.team_id ?? '',
    avatar: user.avatar ?? '',
    emailSignature: '',
    // Blank-but-UNKNOWN until GET /admin/users/:id/email-signature answers.
    signatureLoaded: false,
    mailbox: {
      imapHost: mb?.imap_host ?? '',
      imapPort: numField(mb?.imap_port),
      imapUser: mb?.imap_user ?? '',
      // Secrets never come back from the API (T02): kept blank ("keep current").
      imapPassword: '',
      smtpHost: mb?.smtp_host ?? '',
      smtpPort: numField(mb?.smtp_port),
      smtpUser: mb?.smtp_user ?? '',
      smtpPassword: '',
    },
  };
}

/**
 * Adopt the signature that `GET /admin/users/:id/email-signature` returned into an
 * open dialog form (E10-F04-T10 — the "reopening Settings shows a blank editor" bug).
 *
 * Idempotent and LATE-SAFE: once `signatureLoaded` is true the form is returned
 * UNCHANGED (same reference, so a React `setForm` bails out of the re-render). That
 * covers the two orders that would otherwise destroy work — a second response for the
 * same dialog, and a slow response landing AFTER the admin already typed
 * ({@link editedSignature} raises the flag, so their text wins over the server's).
 */
export function applyLoadedSignature(form: UserForm, html: string): UserForm {
  if (form.signatureLoaded) return form;
  return { ...form, emailSignature: html, signatureLoaded: true };
}

/**
 * The admin edited the signature by hand: their text becomes the value AND is
 * authoritative — `signatureLoaded` is raised so a late fetch cannot overwrite it
 * (see {@link applyLoadedSignature}) and so a deliberate CLEAR is actually saved.
 *
 * A change that is materially a NO-OP leaves the form completely untouched (same
 * reference, flag NOT raised). That is not an optimisation, it is required: TipTap's
 * `setEditable()` emits an `update` event unconditionally, so `RichTextEditor` fires
 * `onChange` with its current (still empty) HTML on mount and on every disabled↔
 * enabled flip — without this guard that phantom "edit" would mark the blank
 * placeholder authoritative BEFORE the fetch answers, and the save would wipe the
 * stored signature. Exactly the bug E10-F04-T10 is about.
 */
export function editedSignature(form: UserForm, html: string): UserForm {
  if (isSameRichText(html, form.emailSignature)) return form;
  return { ...form, emailSignature: html, signatureLoaded: true };
}

/**
 * Whether the IMAP/SMTP section starts expanded for the given user. Legacy:
 * `if (sec && mb.imap_host) sec.classList.remove('hidden')` — expanded on Edit iff
 * an imap_host is already configured (T04 acceptance #3). Always collapsed on Add.
 */
export function mailboxIsExpanded(user: AdminUserDTO | null): boolean {
  return Boolean(user?.mailbox?.imap_host && user.mailbox.imap_host.trim().length > 0);
}

/** True when the mailbox sub-form has a real (non-blank) IMAP host (T04 trigger). */
export function mailboxHasImapHost(mailbox: UserMailboxForm): boolean {
  return mailbox.imapHost.trim().length > 0;
}

/**
 * Build the `mailbox` object the request carries, mirroring the legacy `saveUser`
 * collection (parseInt(port)||0, every field trimmed). Only the fields the user
 * filled are sent; an empty password is left out (so the server's "keep current"
 * applies). Returns `undefined` when imap_host is blank — the caller then omits
 * `mailbox` entirely so the imap row is not touched (T04 acceptance #2).
 */
export function mailboxFromForm(
  mailbox: UserMailboxForm,
): NonNullable<CreateAdminUserInput['mailbox']> | undefined {
  if (!mailboxHasImapHost(mailbox)) return undefined;
  const out: NonNullable<CreateAdminUserInput['mailbox']> = {
    imap_host: mailbox.imapHost.trim(),
  };
  const imapPort = parseInt(mailbox.imapPort, 10);
  if (imapPort) out.imap_port = imapPort;
  if (mailbox.imapUser.trim()) out.imap_user = mailbox.imapUser.trim();
  if (mailbox.imapPassword) out.imap_password = mailbox.imapPassword;
  if (mailbox.smtpHost.trim()) out.smtp_host = mailbox.smtpHost.trim();
  const smtpPort = parseInt(mailbox.smtpPort, 10);
  if (smtpPort) out.smtp_port = smtpPort;
  if (mailbox.smtpUser.trim()) out.smtp_user = mailbox.smtpUser.trim();
  if (mailbox.smtpPassword) out.smtp_password = mailbox.smtpPassword;
  return out;
}

/**
 * Build the create/update request body from the dialog form (legacy `saveUser`
 * userData assembly). name/email/avatar/team_id trimmed; team_id/avatar → null
 * when blank; `password` sent ONLY when non-empty (Edit "Leave empty to keep
 * current"; Add requires it via the component); the `mailbox` object attached only
 * when imap_host is filled. The same shape serves both POST (create) and PATCH
 * (update).
 *
 * `email_signature` is sent ONLY when `signatureLoaded` is true (E10-F04-T10). The
 * key is optional in both contracts, so omitting it means "keep the stored
 * signature" — which is the ONLY safe thing to send while the editor holds a blank
 * placeholder because the per-user fetch has not answered (or failed). When the flag
 * IS set the value goes out even if empty: clearing a signature must work.
 */
export function bodyFromForm(form: UserForm): CreateAdminUserInput & UpdateAdminUserInput {
  const body: CreateAdminUserInput & UpdateAdminUserInput = {
    name: form.name.trim(),
    email: form.email.trim(),
    role: form.role,
    team_id: form.teamId.trim() || null,
    avatar: form.avatar.trim() || null,
  };
  if (form.signatureLoaded) body.email_signature = form.emailSignature.trim();
  if (form.password) body.password = form.password;
  const mailbox = mailboxFromForm(form.mailbox);
  if (mailbox) body.mailbox = mailbox;
  return body;
}
